
import { computed, defineComponent, onMounted, PropType, reactive, ref, toRefs } from 'vue'
import { CtCatalogue, CtClient, CtDatamap, CtShipmentCarrier, CtShipmentCarrierGroup } from '@/models/interfaces/content-type'
import useContentModule from '@/pages/composables/useContentModule'
import { useStore } from 'vuex'
import EditableBlock from '@/components/EditableBlock.vue'
import EditableBlockInput from '@/components/EditableBlockInput.vue'
import { useQuasar } from 'quasar'
import { SHIPPING_CARRIERS } from '@/constants'
import Directr from '@/services/directr'
import Tooltip from '@/components/Tooltip.vue'
import tooltips from '@/constants/tooltips'
import TickIcon from '@/components/Icons/TickIcon.vue'
import CloseIcon from '@/components/Icons/CloseIcon.vue'
import DButton from '@/components/base/DButton.vue'
import Clipable from '@/components/Clipable.vue'

import CryptoJS from 'crypto-js'

export const CHANNEL_API_KEY_PRE_ID = 'OZNCT'

export default defineComponent({
  name: 'ChannelSettings',
  components: { EditableBlockInput, EditableBlock, Tooltip, CloseIcon, TickIcon, DButton, Clipable },
  props: {
    catalogue: {
      type: Object as PropType<CtCatalogue>,
      required: true
    },
    readonly: {
      type: Boolean
    }
  },
  emits: ['update:catalogue'],
  methods: {
    resetCarrierGroup () {
      this.carrierGroup.process_initial = false
      this.carrierGroup.process_order = []
      this.carrierGroup.name = ''
    }
  },
  setup (props, { emit }) {
    const $q = useQuasar()
    const $store = useStore()
    const $directr: Directr = $store.getters['Services/Directr']
    const $sys = $store.getters['Services/Sys']
    const state = reactive({
      showTokenModal: false,
      showCreateCarrierGroup: false,
      ShipmentCarriers: [] as Array<CtShipmentCarrier>,
      ShipmentCarrierGroups: [] as Array<CtShipmentCarrierGroup>,
      ShipmentServices: [] as any,
      carrierGroup: { process_initial: false } as CtShipmentCarrierGroup,
      selectedCarrierGroup: { label: '', value: '', type: '' }
    })


    const channelToken = computed(() => props.catalogue?.token?.access_token || `${CHANNEL_API_KEY_PRE_ID}-${CryptoJS.SHA256(String(Math.round(Math.random())+Date.now()))}`)

    const { items: clients, fetch: fetchClients } = useContentModule<CtClient>('Clients', $store)
    void fetchClients([{ select: ['name'] }])
    const clientOptions = computed(() => clients.value?.filter(c => c.name || c.id).map((c) => ({ label: c.name || c.id, value: c.sys.id })))

    const filteredFormattedCarrierList = ref()

    const currencyList = ref([
      { label: 'EUR', value: 'EUR' },
      { label: 'USD', value: 'USD' },
      { label: 'BRL', value: 'BRL' },
    ])

    const { items: datamaps, fetch: fetchDatamap } = useContentModule<CtDatamap>('Datamap', $store)
    void fetchDatamap([{ select: ['name'] }])
    const datamapOptions = computed(() => !datamaps.value ? { label: 'No data map', value: null } : [
      ...datamaps.value.map((dm) => ({ label: dm.name ?? 'Untitled', value: dm.sys.id })),
      { label: 'No data map', value: null }
    ])

    onMounted(() => {
      void getShipmentValues()
    })

    const resetCarrierGroup = () => {
      state.carrierGroup.process_initial = false
      state.carrierGroup.process_order = []
      state.carrierGroup.name = ''
    }
    const getShipmentValues = async () => {
      const carriers = await $directr.get('shipment_carrier', []).then(({ items }) => items as CtShipmentCarrier[])
      const carrierGroups = await $directr.get('shipment_carrier_group', []).then(({ items }) => items as CtShipmentCarrierGroup[])
      state.ShipmentCarriers = carriers
      state.ShipmentCarrierGroups = carrierGroups
      state.ShipmentServices = await Promise.allSettled(carriers.map(async (carrier) => (!carrier.service ? false : await $sys.get(`/service/${carrier.service}/config`))))
        .then(settled => settled.filter(s => (s as any).value).filter(Boolean))
    }

    const formattedCarriers = computed(() => [
      ...state.ShipmentCarriers.map(p => ({ ...p, type: 'Carrier' })),
      ...state.ShipmentCarrierGroups.map(c => ({ ...c, type: 'Carrier Group' }))
    ].map((obj) => ({
      value: obj.sys.id,
      label: obj.name?.toString(),
      type: obj.type
    }))
    )
    const filterFn = (val, update) => {
      update(() => {
        if (!val?.length) {
          filteredFormattedCarrierList.value = formattedCarriers.value
        } else {
          const needle = val.toLowerCase()
          filteredFormattedCarrierList.value = formattedCarriers.value.filter(fp => (fp.label ?? '').toLowerCase().indexOf(needle) > -1)
        }
      })
    }

    async function saveCarrierGroup (carrierGroup, editState) {
      try {
        const group = await $directr.put('shipment_carrier_group', {...carrierGroup, shipping_carriers: carrierGroup.process_order}).then(({ items }) => items?.[0] ?? null)
        $q.notify({ type: 'positive', message: `${!carrierGroup.sys?.created_at ? 'Created' : 'Updated'} carrier-group ${carrierGroup.name || carrierGroup?.sys?.id}` })

        state.selectedCarrierGroup = {
          label: group.name,
          value: group.sys.id,
          type: 'Carrier Group'
        }

        editState.shipping_carrier = state.selectedCarrierGroup
        void getShipmentValues()
        state.showCreateCarrierGroup = false
        resetCarrierGroup()

        // emit update to trigger save & update root
        emit('update:catalogue', props.catalogue?.sys?.id, { shipping_carrier: state.selectedCarrierGroup.value })
      } catch (e) {
        console.log('error', e)
        resetCarrierGroup()
        if (!carrierGroup.name && !carrierGroup.process_order)
          $q.notify({ type: 'negative', message: 'Carrier-group name and/or carriers cannot be empty' })
        else
          $q.notify({ type: 'negative', message: `Carrier-group ${carrierGroup.name || 'unknown name'} could not be created because an error occurred.` })
      }
    }

    const onCarrierGroupClick = (opt) => {
      const selectedCarrierGroup = state.ShipmentCarrierGroups.find(f => f.sys.id === opt.value)
      // const carrierMap = state.ShipmentCarriers.map(s => { return { value: s.sys.id, label: s.name } })
      state.carrierGroup = {
        ...state.carrierGroup,
        sys: selectedCarrierGroup?.sys as any,
        process_initial: selectedCarrierGroup?.process_initial,
        name: selectedCarrierGroup?.name || '',
        process_order: selectedCarrierGroup?.process_order
      }
      state.showCreateCarrierGroup = true
    }

    const selectedCarriers = computed(() => {
      return state.carrierGroup.process_order?.map(c => {
        const match = state.ShipmentCarriers.find(n => n.sys.id === c)
        return { label: match?.name, value: match?.sys.id }
      }) || []
    })

    function updateGroupCarriers ($event) {
      const value = $event.map(s => s.value).filter(Boolean)
      state.carrierGroup.process_order = value
      state.carrierGroup.shipment_carriers = value
    }

    function showChannelTokenModal () {
      state.showTokenModal = true
    }

    function clearChannelToken () {
      state.showTokenModal = false

      $q.dialog({
        title: 'Remove Channel API Key?',
        message: 'This will remove the current API key for this Channel. This action cannot be undone',
        cancel: true,
      }).onCancel(() => false).onDismiss(() => false).onOk(() => {
        emit('update:catalogue', props.catalogue.sys.id, { token: { access_token: null } })
      })
    }

    function regenerateChannelToken () {
      state.showTokenModal = false
      $q.dialog({
        title: 'Regenerate Channel API Key?',
        message: 'This will remove the current API key for this Channel and generate a new one. This action cannot be undone',
        cancel: true,
      }).onCancel(() => false).onDismiss(() => false).onOk(() => {
        emit('update:catalogue', props.catalogue.sys.id, { token: { access_token: null } })
        state.showTokenModal = true
      })
    }

    function saveChannelToken (access_token) {
      state.showTokenModal = false
      emit('update:catalogue', props.catalogue.sys.id, { token: { access_token } })
    }

    return {
      ...toRefs(state),
      channelToken,
      saveChannelToken,
      regenerateChannelToken,
      clearChannelToken,
      showChannelTokenModal,
      selectedCarriers,
      updateGroupCarriers,
      SHIPPING_CARRIERS,
      clientOptions,
      datamapOptions,
      filteredFormattedCarrierList,
      formattedCarriers,
      filterFn,
      saveCarrierGroup,
      onCarrierGroupClick,
      currencyList,
      clipped: () => {
        $q.notify({ message: 'Copied to clipboard', position: 'top', classes: 'custom-notify' })
      },
    }
  },

})
